/**
 * Custom event catalog shared by the frontend and Node-based export tooling.
 *
 * Keep this module as plain JavaScript data with no TypeScript runtime
 * dependency. The TypeScript wrapper validates the catalog shape and derives
 * the application-facing literal types.
 */

/** Custom event parameter names and their GA4-facing descriptions. */
export const CUSTOM_EVENT_PARAMETER_DEFINITIONS = /** @type {const} */ ({
  employees: {
    description: "JADE 社内ユーザーかどうか。yes / no / unknown で送る。",
  },
  plan: { description: "契約プラン名。取得不可なら unknown。" },
  workspace_role: {
    description: "event 発火時点の workspace role。取得不可なら unknown。",
  },
  connection_method: { description: "外部 data source の接続方法。" },
  connection_scope: {
    description: "接続設定が workspace 全体か project 単位か。",
  },
  connection_context: { description: "接続が発生した設定 surface。" },
  product_area: {
    description:
      "sa / ua / iw / dashboard / ai / settings / workspace などの領域。",
  },
  export_format: { description: "export/copy された形式。" },
  invite_role: { description: "招待した user role。" },
  settings_area: { description: "保存した settings 領域。" },
  notification_type: { description: "開いた notification の種類。" },
  content_group: { description: "既存 page_view と同じ画面分類。" },
});

/** Parameters attached to every custom event by the analytics gateway. */
export const CUSTOM_EVENT_COMMON_PARAMETER_NAMES = /** @type {const} */ ([
  "employees",
  "product_area",
  "content_group",
  "plan",
  "workspace_role",
]);

/** Implemented custom events. Future candidates do not belong here. */
export const CUSTOM_EVENT_DEFINITIONS = /** @type {const} */ ({
  workspace_setup_completed: {
    description: "workspace setup 完了を測る。",
    keyEventCandidate: true,
    parameters: [],
  },
  workspace_created: {
    description: "signup 後の初回 activation を測る。",
    keyEventCandidate: true,
    parameters: [],
  },
  ga4_connected: {
    description: "GA4 連携完了を測る。",
    keyEventCandidate: true,
    parameters: [
      "product_area",
      "connection_method",
      "connection_scope",
      "connection_context",
    ],
  },
  search_console_connected: {
    description: "Search Console 連携完了を測る。",
    keyEventCandidate: true,
    parameters: [
      "product_area",
      "connection_method",
      "connection_scope",
      "connection_context",
    ],
  },
  iw_project_created: {
    description: "Index Worker 利用開始を測る。",
    keyEventCandidate: true,
    parameters: ["product_area"],
  },
  ua_project_created: {
    description: "User Analytics 利用開始を測る。",
    keyEventCandidate: true,
    parameters: ["product_area"],
  },
  sa_project_created: {
    description: "Search Analytics 利用開始を測る。",
    keyEventCandidate: true,
    parameters: ["product_area"],
  },
  terms_agreement_accepted: {
    description: "利用規約への同意完了を測る。",
    parameters: [],
  },
  view_saved: {
    description: "view の新規作成・更新の保存完了を測る。",
    parameters: ["product_area"],
  },
  report_exported: {
    description:
      "download / copy / image / 非同期 export 受付など、出力操作の成功を測る。",
    parameters: ["product_area", "export_format"],
  },
  dashboard_created: {
    description: "dashboard 作成を測る。",
    keyEventCandidate: true,
    parameters: ["product_area"],
  },
  ai_chat_started: {
    description: "AI chat 利用開始を測る。",
    parameters: [],
  },
  ai_response_exported: {
    description: "AI response の業務利用を測る。",
    parameters: ["export_format"],
  },
  invite_sent: {
    description: "team 展開につながる invite を測る。",
    parameters: ["invite_role"],
  },
  settings_updated: {
    description: "settings 保存を領域別に測る。",
    parameters: ["settings_area"],
  },
  notification_opened: {
    description: "通知起点の再訪を測る。",
    parameters: ["notification_type"],
  },
  key_event_configured: {
    description: "GA4 key event 設定完了を測る。",
    keyEventCandidate: true,
    parameters: ["settings_area"],
  },
});
